import {
    ArrowBigDownDashIcon,
    BadgeTurkishLiraIcon,
    BanknoteArrowDownIcon,
    BellDotIcon,
    Bolt,
    ChartNoAxesCombinedIcon,
    HistoryIcon,
    Pill,
    PlusCircleIcon,
    RatioIcon,
    ShoppingCartIcon,
    StoreIcon,
    User,
    UsersRoundIcon,
    WarehouseIcon,
} from 'lucide-react';

import { Route } from '@/constants';
import { PERMISSION_TYPE, RESOURCE } from '@/constants/resource.constant';

interface MenuItemType {
    label: string;
    icon: JSX.Element;
    path: string;
    permissions: {
        resource: (typeof RESOURCE)[keyof typeof RESOURCE];
        action: (typeof PERMISSION_TYPE)[keyof typeof PERMISSION_TYPE];
    }[];
}

interface MenuType {
    label: string;
    icon: JSX.Element;
    children: MenuItemType[];
}

export const SIDEBAR_MENU: MenuType[] = [
    {
        label: 'Products',
        icon: <Pill />,
        children: [
            {
                label: 'All Products',
                path: Route.PRODUCTS,
                icon: <Pill />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },

            {
                label: 'Create New Product',
                path: Route.PRODUCT_CREATE_NEW,
                icon: <PlusCircleIcon />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Create,
                    },
                ],
            },

            {
                label: 'Brand',
                path: Route.PRODUCT_BRANDS,
                icon: <Pill />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Unit',
                path: Route.PRODUCT_UNITS,
                icon: <Pill />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Types',
                path: Route.PRODUCT_TYPES,
                icon: <Pill />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Strength',
                path: Route.PRODUCT_STRENGTHS,
                icon: <Pill />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Generics',
                path: Route.PRODUCT_GENERICS,
                icon: <Pill />,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Stock',
        icon: <StoreIcon />,
        children: [
            {
                label: 'All Stock',
                icon: <StoreIcon />,
                path: Route.STOCK_ALL_PRODUCTS,
                permissions: [
                    {
                        resource: RESOURCE.INVENTORY,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Low Stock',
                icon: <ArrowBigDownDashIcon />,
                path: Route.STOCK_LOW_PRODUCTS,
                permissions: [
                    {
                        resource: RESOURCE.INVENTORY,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Stock Out',
                icon: <StoreIcon />,
                path: Route.STOCK_OUT_PRODUCTS,
                permissions: [
                    {
                        resource: RESOURCE.INVENTORY,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },

            {
                label: 'Expired Stocks',
                icon: <StoreIcon />,
                path: Route.STOCK_EXPIRED_PRODUCTS,
                permissions: [
                    {
                        resource: RESOURCE.INVENTORY,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Adjustments',
        icon: <RatioIcon />,
        children: [
            {
                label: 'Expired Items',
                icon: <RatioIcon />,
                path: Route.ADJUSTMENT_EXPIRED_PRODUCTS,
                permissions: [
                    {
                        resource: RESOURCE.ADJUSTMENT,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Stock Adjustment',
                icon: <RatioIcon />,
                path: Route.ADJUSTMENT_STOCKS,
                permissions: [
                    {
                        resource: RESOURCE.ADJUSTMENT,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Sales',
        icon: <BadgeTurkishLiraIcon />,
        children: [
            {
                label: 'All Sales',
                icon: <BadgeTurkishLiraIcon />,
                path: Route.SALES,
                permissions: [
                    { resource: RESOURCE.ORDERS, action: PERMISSION_TYPE.Read },
                ],
            },
            {
                label: 'Create New Sale',
                icon: <PlusCircleIcon />,
                path: Route.POS_NEW,
                permissions: [
                    {
                        resource: RESOURCE.ORDERS,
                        action: PERMISSION_TYPE.Create,
                    },
                ],
            },
            {
                label: 'Sales Return',
                icon: <BadgeTurkishLiraIcon />,
                path: Route.SALES_RETURNS,
                permissions: [
                    { resource: RESOURCE.RETURN, action: PERMISSION_TYPE.Read },
                ],
            },
        ],
    },
    {
        label: 'Purchases',
        icon: <ShoppingCartIcon />,
        children: [
            {
                label: 'All Purchases',
                icon: <ShoppingCartIcon />,
                path: Route.PURCHASES,
                permissions: [
                    {
                        resource: RESOURCE.PURCHASES,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Create New Purchase',
                icon: <PlusCircleIcon />,
                path: Route.PRE_PURCHASE_CREATE,
                permissions: [
                    {
                        resource: RESOURCE.PURCHASES,
                        action: PERMISSION_TYPE.Create,
                    },
                ],
            },

            {
                label: 'Purchase Returns',
                icon: <ShoppingCartIcon />,
                path: Route.PURCHASE_RETURNS,
                permissions: [
                    {
                        resource: RESOURCE.PURCHASES,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },

            {
                label: 'Pre-Purchase List',
                icon: <ShoppingCartIcon />,
                path: Route.PRE_PURCHASES,
                permissions: [
                    {
                        resource: RESOURCE.PURCHASES,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },

            // {
            //     label: 'Create Pre-Purchase',
            //     icon: <PlusCircleIcon />,
            //     path: Route.PRE_PURCHASE_CREATE,
            //     permissions: [
            //         {
            //             resource: Resource.PURCHASES,
            //             action: PermissionType.Read,
            //         },
            //     ],
            // },
        ],
    },
    {
        label: 'Expenses',
        icon: <BanknoteArrowDownIcon />,
        children: [
            {
                label: 'All Expenses',
                icon: <BanknoteArrowDownIcon />,
                path: Route.EXPENSES,
                permissions: [
                    {
                        resource: RESOURCE.EXPENSE,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Expense Categories',
                icon: <BanknoteArrowDownIcon />,
                path: Route.EXPENSE_CATEGORIES,
                permissions: [
                    {
                        resource: RESOURCE.EXPENSE,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Reports and Analytics',
        icon: <ChartNoAxesCombinedIcon />,
        children: [
            {
                label: 'Profit and Loss',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_PROFIT_AND_LOSS,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'All Transactions',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_ALL_TRANSACTIONS,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },

            // {
            //     label: 'Cash Box',
            //     icon: <ChartNoAxesCombinedIcon />,
            //     path: Route.CASH_BOX,
            //     permissions: [
            //         {
            //             resource: RESOURCE.REPORTS,
            //             action: PERMISSION_TYPE.Read,
            //         },
            //     ],
            // },

            {
                label: 'Purchase Report',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_PURCHASE,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Sale Report',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_SALES,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Customer Report',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_CUSTOMERS,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Supplier Report',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_SUPPLIERS,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            // {
            //     label: 'Stock Report',
            //     icon: <User />,
            //     path: Route.REPORTS_STOCK,
            //     permissions: [
            //         { resource: Resource.REPORTS, action: PermissionType.Read },
            //     ],
            // },
            // {
            //     label: 'Stock Adjustment Report',
            //     icon: <User />,
            //     path: Route.REPORTS_STOCK_ADJUSTMENT,
            //     permissions: [
            //         { resource: Resource.REPORTS, action: PermissionType.Read },
            //     ],
            // },
            // {
            //     label: 'Purchase Payment Report',
            //     icon: <User />,
            //     path: Route.REPORTS_PURCHASE_PAYMENT,
            //     permissions: [
            //         { resource: Resource.REPORTS, action: PermissionType.Read },
            //     ],
            // },
            // {
            //     label: 'Sale Payment Report',
            //     icon: <User />,
            //     path: Route.REPORTS_SALE_PAYMENT,
            //     permissions: [
            //         { resource: Resource.REPORTS, action: PermissionType.Read },
            //     ],
            // },
            {
                label: 'Expense Report',
                icon: <ChartNoAxesCombinedIcon />,
                path: Route.REPORTS_EXPENSE,
                permissions: [
                    {
                        resource: RESOURCE.REPORTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            // {
            //     label: 'Daily Report',
            //     icon: <User />,
            //     path: Route.REPORTS_DAILY,
            //     permissions: [
            //         { resource: RESOURCE.REPORTS, action: PERMISSION_TYPE.Read },
            //     ],
            // },
            // {
            //     label: 'Register Report',
            //     icon: <User />,
            //     path: Route.REPORTS_REGISTER,
            //     permissions: [
            //         { resource: Resource.REPORTS, action: PermissionType.Read },
            //     ],
            // },
            // {
            //     label: 'Activity Logs',
            //     icon: <User />,
            //     path: Route.REPORTS_ACTIVITY_LOGS,
            //     permissions: [
            //         { resource: Resource.REPORTS, action: PermissionType.Read },
            //     ],
            // },
        ],
    },
    {
        label: 'Wholesale',
        icon: <WarehouseIcon />,
        children: [
            {
                label: 'New Orders',
                icon: <PlusCircleIcon />,
                path: Route.WHOLESALE_ORDER_REQUESTS,
                permissions: [
                    {
                        resource: RESOURCE.WHOLESALE,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Wholesale Customers',
                icon: <UsersRoundIcon />,
                path: Route.WHOLESALE_ORDER_CUSTOMERS,
                permissions: [
                    {
                        resource: RESOURCE.WHOLESALE,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Order History',
                icon: <HistoryIcon />,
                path: `${Route.SALES}?customer_type=wholesale`,
                permissions: [
                    {
                        resource: RESOURCE.WHOLESALE,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Contacts',
        icon: <UsersRoundIcon />,
        children: [
            {
                label: 'Customers',
                icon: <UsersRoundIcon />,
                path: Route.CUSTOMERS,
                permissions: [
                    {
                        resource: RESOURCE.CUSTOMERS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
            {
                label: 'Suppliers',
                icon: <UsersRoundIcon />,
                path: Route.SUPPLIERS,
                permissions: [
                    {
                        resource: RESOURCE.SUPPLIERS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Users',
        icon: <User />,
        children: [
            {
                label: 'All Users',
                icon: <User />,
                path: Route.USERS,
                permissions: [
                    { resource: RESOURCE.USERS, action: PERMISSION_TYPE.Read },
                ],
            },
            // {
            //     label: 'Create New User',
            //     icon: <User />,
            //     path: Route.USER_CREATE,
            //     permissions: [
            //         { resource: Resource.USERS, action: PermissionType.Create },
            //     ],
            // },
            {
                label: 'Roles and Permissions',
                icon: <User />,
                path: Route.ROLES,
                permissions: [
                    { resource: RESOURCE.USERS, action: PERMISSION_TYPE.Read },
                ],
            },
        ],
    },
    {
        label: 'Notifications',
        icon: <BellDotIcon />,
        children: [
            {
                label: 'All Notifications',
                icon: <BellDotIcon />,
                path: Route.NOTIFICATIONS,
                permissions: [
                    {
                        resource: RESOURCE.PRODUCTS,
                        action: PERMISSION_TYPE.Read,
                    },
                ],
            },
        ],
    },
    {
        label: 'Settings',
        icon: <Bolt />,
        children: [
            {
                label: 'Invoice Settings',
                icon: <Bolt />,
                path: Route.SETTINGS_INVOICE,
                permissions: [
                    {
                        resource: RESOURCE.SETTINGS,
                        action: PERMISSION_TYPE.Update,
                    },
                ],
            },
        ],
    },
];
